import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Building2, Users, AlertCircle, CheckCircle2, TrendingUp } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
// PAGE 7: Deal snapshot
export function Page7() {
  return <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto w-full" data-id="element-405">
      <motion.div variants={fadeUp} className="mb-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-sm font-medium text-gray-600" data-id="element-406">
        <Building2 className="w-4 h-4" data-id="element-407" /> Deal Review
      </motion.div>
      <motion.h2 variants={fadeUp} className="text-4xl font-bold mb-10" data-id="element-408">
        CDS - High 6-figures ACV
      </motion.h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-409">
        <motion.div variants={fadeUp} className="bg-white p-6 rounded-2xl sierra-shadow border border-gray-100" data-id="element-410">
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4" data-id="element-411">
            The Landscape
          </h3>
          <ul className="space-y-4" data-id="element-412">
            <li className="flex justify-between items-center border-b border-gray-50 pb-3" data-id="element-413">
              <span className="text-gray-600" data-id="element-414">Customer</span>
              <span className="font-semibold" data-id="element-415">CDS (Retail group)</span>
            </li>
            <li className="flex justify-between items-center border-b border-gray-50 pb-3" data-id="element-416">
              <span className="text-gray-600" data-id="element-417">Brands</span>
              <span className="font-semibold" data-id="element-418">The Range, Homebase, Wilko</span>
            </li>
            <li className="flex justify-between items-center pb-1" data-id="element-419">
              <span className="text-gray-600" data-id="element-420">Context</span>
              <span className="font-medium text-right max-w-[200px]" data-id="element-421">
                Incumbent competitor at 2 brands. Attentive used for SMS at 1.
              </span>
            </li>
          </ul>
        </motion.div>

        <motion.div variants={fadeUp} className="bg-white p-6 rounded-2xl sierra-shadow border border-gray-100" data-id="element-422">
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-423">
            <Users className="w-4 h-4" data-id="element-424" /> Buying Committee
          </h3>
          <ul className="space-y-4" data-id="element-425">
            <li className="flex flex-col border-b border-gray-50 pb-3" data-id="element-426">
              <span className="text-xs text-sierra-blue font-semibold uppercase" data-id="element-427">
                Economic Buyer
              </span>
              <span className="font-semibold text-lg" data-id="element-428">
                Head of Digital (Ben)
              </span>
            </li>
            <li className="flex flex-col border-b border-gray-50 pb-3" data-id="element-429">
              <span className="text-xs text-emerald-600 font-semibold uppercase" data-id="element-430">
                Champion
              </span>
              <span className="font-semibold" data-id="element-431">CRM Director (George)</span>
            </li>
            <li className="flex flex-col pb-1" data-id="element-432">
              <span className="text-xs text-amber-600 font-semibold uppercase" data-id="element-433">
                Key Stakeholder
              </span>
              <span className="font-semibold" data-id="element-434">
                Digital Marketing Director (Rosanna)
              </span>
            </li>
          </ul>
        </motion.div>
      </div>
    </motion.div>;
}
// PAGE 8: Obstacles + what I did
export function Page8() {
  return <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-5xl mx-auto w-full" data-id="element-435">
      <motion.h2 variants={fadeUp} className="text-3xl font-bold mb-10 text-center" data-id="element-436">
        Navigating the Incumbent Advantage
      </motion.h2>

      <div className="space-y-8" data-id="element-437">
        {/* Obstacle 1 */}
        <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-12 gap-6 bg-white rounded-2xl sierra-shadow border border-gray-100 overflow-hidden" data-id="element-438">
          <div className="md:col-span-4 bg-red-50 p-6 border-r border-gray-100" data-id="element-439">
            <div className="flex items-center gap-2 text-red-700 font-semibold mb-3" data-id="element-440">
              <AlertCircle className="w-5 h-5" data-id="element-441" /> Obstacle 1
            </div>
            <p className="text-gray-800 font-medium" data-id="element-442">
              Competitor incumbent = lower perceived risk and migration cost.
            </p>
          </div>
          <div className="md:col-span-8 p-6" data-id="element-443">
            <div className="flex items-center gap-2 text-sierra-blue font-semibold mb-3" data-id="element-444">
              <CheckCircle2 className="w-5 h-5" data-id="element-445" /> The Motion
            </div>
            <p className="text-gray-600" data-id="element-446">
              Over-indexed on migration plan + support posture; used
              large-client proof to de-risk execution.
            </p>
          </div>
        </motion.div>

        {/* Obstacle 2 */}
        <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-12 gap-6 bg-white rounded-2xl sierra-shadow border border-gray-100 overflow-hidden" data-id="element-447">
          <div className="md:col-span-4 bg-red-50 p-6 border-r border-gray-100" data-id="element-448">
            <div className="flex items-center gap-2 text-red-700 font-semibold mb-3" data-id="element-449">
              <AlertCircle className="w-5 h-5" data-id="element-450" /> Obstacle 2
            </div>
            <p className="text-gray-800 font-medium" data-id="element-451">
              Head of Digital didn't see us as an email provider; strong
              competitor bias.
            </p>
          </div>
          <div className="md:col-span-8 p-6" data-id="element-452">
            <div className="flex items-center gap-2 text-sierra-blue font-semibold mb-3" data-id="element-453">
              <CheckCircle2 className="w-5 h-5" data-id="element-454" /> The Motion
            </div>
            <ul className="space-y-3 text-gray-600" data-id="element-455">
              <li className="flex items-start gap-2" data-id="element-456">
                <span className="text-sierra-blue mt-1" data-id="element-457">•</span>
                <span data-id="element-458">Over-indexed on capability + ROI.</span>
              </li>
              <li className="flex items-start gap-2" data-id="element-459">
                <span className="text-sierra-blue mt-1" data-id="element-460">•</span>
                <span data-id="element-461">
                  Ran a TEI-style study proving existing results and how they'd
                  replicate across brands.
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-462">
                <span className="text-sierra-blue mt-1" data-id="element-463">•</span>
                <span data-id="element-464">
                  Built personalised demo videos and RFP content addressing
                  cross-brand engagement.
                </span>
              </li>
            </ul>
          </div>
        </motion.div>
      </div>
    </motion.div>;
}
// PAGE 9: Turning point + outcome
export function Page9() {
  return <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-5xl mx-auto w-full" data-id="element-465">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-466">
        <div className="space-y-8" data-id="element-467">
          <motion.div variants={fadeUp} data-id="element-468">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3" data-id="element-469">
              The Turning Point
            </h3>
            <blockquote className="text-xl italic text-gray-700 border-l-4 border-sierra-blue pl-4 py-1" data-id="element-470">
              "Most put together, most thorough, most compelling RFP response."
            </blockquote>
          </motion.div>

          <motion.div variants={fadeUp} className="bg-emerald-50 border border-emerald-100 rounded-2xl p-6" data-id="element-471">
            <h3 className="text-emerald-800 font-bold text-xl flex items-center gap-2 mb-2" data-id="element-472">
              <TrendingUp className="w-6 h-6" data-id="element-473" /> WON: High 6-figure ACV
            </h3>
            <p className="text-emerald-700 font-medium" data-id="element-474">
              Why we won: reduced migration risk AND proved superior ROI with
              tailored evidence.
            </p>
          </motion.div>

          <motion.div variants={fadeUp} data-id="element-475">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4" data-id="element-476">
              Lessons Learned
            </h3>
            <ul className="space-y-4" data-id="element-477">
              <li className="flex items-start gap-3 bg-white p-4 rounded-xl border border-gray-100 sierra-shadow" data-id="element-478">
                <span className="text-sierra-blue font-bold mt-0.5" data-id="element-479">1</span>
                <p className="text-sm text-gray-700" data-id="element-480">
                  In incumbent situations, win by making change feel
                  operationally safe and inevitable.
                </p>
              </li>
              <li className="flex items-start gap-3 bg-white p-4 rounded-xl border border-gray-100 sierra-shadow" data-id="element-481">
                <span className="text-sierra-blue font-bold mt-0.5" data-id="element-482">2</span>
                <p className="text-sm text-gray-700" data-id="element-483">
                  Proof {'>'} persuasion: tailored ROI + impact stories
                  outperform feature talk.
                </p>
              </li>
              <li className="flex items-start gap-3 bg-white p-4 rounded-xl border border-gray-100 sierra-shadow" data-id="element-484">
                <span className="text-sierra-blue font-bold mt-0.5" data-id="element-485">3</span>
                <p className="text-sm text-gray-700" data-id="element-486">
                  Design the narrative for the sceptic: meet their worldview
                  with evidence and a low-risk plan.
                </p>
              </li>
            </ul>
          </motion.div>
        </div>

        <motion.div variants={fadeUp} className="bg-sierra-dark text-white rounded-3xl p-8 relative overflow-hidden flex flex-col justify-center" data-id="element-487">
          <div className="absolute top-0 right-0 w-64 h-64 bg-green-700 rounded-full blur-3xl opacity-20 -mr-20 -mt-20" data-id="element-488" />
          <div className="relative z-10" data-id="element-489">
            <h3 className="text-green-300 font-semibold tracking-wider uppercase text-sm mb-6" data-id="element-490">
              The Proof Tile
            </h3>
            <div className="space-y-6" data-id="element-491">
              <div data-id="element-492">
                <div className="text-5xl font-bold text-white mb-1" data-id="element-493">10.6x</div>
                <div className="text-gray-400" data-id="element-494">ROI Demonstrated</div>
              </div>
              <div className="w-full h-px bg-gray-800" data-id="element-495" />
              <div className="grid grid-cols-2 gap-6" data-id="element-496">
                <div data-id="element-497">
                  <div className="text-2xl font-semibold text-white" data-id="element-498">963%</div>
                  <div className="text-sm text-gray-400" data-id="element-499">Total ROI</div>
                </div>
                <div data-id="element-500">
                  <div className="text-2xl font-semibold text-white" data-id="element-501">~1 mo</div>
                  <div className="text-sm text-gray-400" data-id="element-502">Payback Period</div>
                </div>
              </div>
              <div className="bg-gray-800/50 p-4 rounded-xl border border-gray-700 mt-4" data-id="element-503">
                <p className="text-sm text-gray-300 leading-relaxed" data-id="element-504">
                  High 7-figure attributable revenue over 12 months vs ~High
                  6-figure annual cost, yielding Mid 7-figure net value.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>;
}